import { errorBox, successBox } from '@core/components/AlertBox';
import { useAppDispatch } from '@core/redux/hooks';
import { RpcRestError } from '@deepup/api-utils';
import { TrashcanOutline, XOutline } from '@deepup/icons';
import { inetworkApiV1 as inetworkApi } from '@deepup/internal-apis';
import { DIALOG_WIDTH } from '@elements/components';
import {
  pointElementActions,
  pointElementSelectors,
  pointElementThunks,
} from '@elements/redux/point-element.slice';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { pipeFittingThunks } from '@elements/redux/pipe-fitting.slice';
import { Connection } from './types';
import {
  useCreateFittingPointElement,
  useUpdateFittingPointElement,
} from './useFittingPointElements';
import { useDeleteFitting } from './usePipeFittings';

const PLANDATA_COMMENT = 'Element labeled according to Plandata';

export const ConnectionEditDialog = ({
  selectedConnection,
  onClose,
}: {
  onClose: () => void;
  selectedConnection: Connection | undefined;
}) => {
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const deleteFitting = useDeleteFitting();
  const { createFittingPointElement } = useCreateFittingPointElement();
  const { updateFittingPointElement } = useUpdateFittingPointElement();
  const dispatch = useAppDispatch();

  const pointElement = useSelector(
    pointElementSelectors.selectById(selectedConnection?.pointElementId),
  );
  const pointElementIsLabeledToPlandata = !!pointElement?.comment?.includes(PLANDATA_COMMENT);
  const pointElementName = pointElement?.name ?? '';
  const [name, setName] = useState(pointElementName);
  const [isLabeledToPlandata, setIsLabeledToPlandata] = useState(pointElementIsLabeledToPlandata);

  if (!selectedConnection) {
    return null;
  }

  const handleSubmit = async () => {
    if (!projectId) {
      return errorBox('Missing project ID');
    }

    const comment = isLabeledToPlandata ? PLANDATA_COMMENT : '';

    try {
      if (!selectedConnection.pointElementId) {
        await createFittingPointElement({
          projectId,
          pointId: selectedConnection.pointId,
          name,
          subType: inetworkApi.PointElementSubType.PIPE_FITTING,
          comment,
          fromSplineElementId: selectedConnection.fromSplineElementId ?? '',
          toSplineElementId: selectedConnection.toSplineElementId ?? '',
        });
      } else {
        await updateFittingPointElement({
          id: selectedConnection.pointElementId,
          type: inetworkApi.PointElementType.FITTING,
          subType: inetworkApi.PointElementSubType.PIPE_FITTING,
          name: name,
          comment: comment,
          surfaceTypeId: '',
          width: 0,
          length: 0,
          depth: 0,
          height: 0,
          labelingConfidence: inetworkApi.LabelingConfidence.UNSPECIFIED,
          orderNumberIdsToAdd: [],
          orderNumberIdsToRemove: [],
        });
      }

      await dispatch(
        pointElementThunks.getMany({
          path: ['projects', projectId, 'pointElements'],
        }),
      ).unwrap();
      await dispatch(
        pipeFittingThunks.getMany({
          path: ['projects', projectId, 'splineElementConnections'],
        }),
      ).unwrap();
      successBox('Successfully updated connection');
      onClose();
    } catch (error) {
      if (error instanceof RpcRestError) {
        errorBox(error.body?.message ?? 'Unknown error');
      } else {
        errorBox('Unknown error');
      }
    }
  };

  return (
    <Dialog
      open={!!selectedConnection}
      PaperProps={{ sx: { width: DIALOG_WIDTH }, elevation: 4 }}
      onClose={onClose}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          Edit Connection
          <IconButton onClick={onClose}>
            <XOutline />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack paddingTop={1} gap={2}>
          <TextField
            onChange={(e) => setName(e.target.value)}
            value={name}
            fullWidth
            required
            label="Connection Name"
          />
          <FormControlLabel
            label="Element labeled according to Plandata"
            control={
              <Checkbox
                checked={isLabeledToPlandata}
                onChange={(e) => setIsLabeledToPlandata(e.target.checked)}
              />
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          justifyContent="space-between"
          direction="row"
          width="100%"
          paddingX={2}
          paddingBottom={2}
        >
          <Button
            color="error"
            variant="outlined"
            startIcon={<TrashcanOutline />}
            onClick={async () => {
              if (!projectId) {
                return errorBox('Missing project ID');
              }
              try {
                await deleteFitting(selectedConnection.id).unwrap();

                if (selectedConnection.pointElementId) {
                  dispatch(pointElementActions.removeOne(selectedConnection.pointElementId));
                }

                await dispatch(
                  pointElementThunks.getMany({
                    path: ['projects', projectId, 'pointElements'],
                  }),
                ).unwrap();
                await dispatch(
                  pipeFittingThunks.getMany({
                    path: ['projects', projectId, 'splineElementConnections'],
                  }),
                ).unwrap();
                successBox('Successfully deleted connection');
                onClose();
              } catch (error) {
                if (error instanceof RpcRestError) {
                  errorBox(error.body?.message ?? 'Unknown error');
                } else {
                  errorBox('Unknown error during deletion');
                }
              }
            }}
          >
            Delete
          </Button>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
